import { useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { useAnalyticsEvents as useAnalyticsEventsNext } from '@atlaskit/teams-app-internal-analytics';
import { usePeopleAndTeamsConfigurationContext } from '@atlassian/people-teams-configuration-client';
import { isSeriousError } from '@atlassian/teams-common/analytics';
import { getTeamProfilePageLinkWithOrgId } from '@atlassian/teams-common/urls';

import useTeamCreateDialogAnalytics from '../../hooks/use-team-create-dialog-analytics';
import { type CreatedTeamData } from '../../types/team';
import { type InvitedUser } from '../../types/user';
import { VOID_FUNC } from '../../utils/constants';
import { type ErrorWithStatus } from '../../utils/fetch';
import {
	AnalyticsSource,
	triggerAnalyticsForClickTeamLinkInSuccessFlag,
	triggerAnalyticsForCreateTeamFailed,
	triggerAnalyticsForCreateTeamSuccess,
} from '../analytics';
import { messages } from '../i18n';

import { type UseTeamCreateConfiguration } from './types';

function useTeamCreateConfiguration(props: UseTeamCreateConfiguration) {
	const {
		product,
		onCreateTeamSuccess = VOID_FUNC,
		onCreateTeamFailed = VOID_FUNC,
		addFlag = VOID_FUNC,
		pushRoute = VOID_FUNC,
		disableSuccessFlag,
		extraAnalyticsAttrs,
		initialMembershipSettings,
		enableMembershipSettingsChoice,
	} = props;
	const peopleAndTeamsConfiguration = usePeopleAndTeamsConfigurationContext();
	const remoteConfigResources = peopleAndTeamsConfiguration?.['people-menu']?.resources || null;
	const { fireEvent } = useAnalyticsEventsNext();

	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		triggerAnalyticsForCreateTeamSuccess: triggerAnalyticsForCreateTeamSuccessNext,
		triggerAnalyticsForCreateTeamFailed: triggerAnalyticsForCreateTeamFailedNext,
	} = useTeamCreateDialogAnalytics();

	const handleOnClickTeamLinkInFlag = useCallback(
		(
			event: React.MouseEvent<HTMLElement, MouseEvent>,
			teamData: CreatedTeamData,
			invitedMembers: InvitedUser[],
		) => {
			event.preventDefault();
			if (fg('ptc-enable-teams-common-analytics-refactor')) {
				fireEvent('ui.teamCreateDialog.clicked.teamLinkSuccessFlag', {
					teamId: teamData.id,
					numberOfMembers: invitedMembers.length,
					...extraAnalyticsAttrs,
				});
			} else {
				triggerAnalyticsForClickTeamLinkInSuccessFlag(
					createAnalyticsEvent,
					teamData.id,
					invitedMembers.length,
					extraAnalyticsAttrs,
				);
			}
			// redirect to team page
			pushRoute(
				getTeamProfilePageLinkWithOrgId({
					remoteConfigResources,
					teamId: teamData.id,
					ref: product,
					src: AnalyticsSource.PEOPLE_TEAMS,
					orgId: props.orgId,
				}),
			);
		},
		[
			createAnalyticsEvent,
			extraAnalyticsAttrs,
			product,
			props.orgId,
			remoteConfigResources,
			pushRoute,
			fireEvent,
		],
	);

	const handleCreateTeamSuccess = useCallback(
		(
			teamData: CreatedTeamData,
			invitedMembers: InvitedUser[],
			failedInvitedMembers?: InvitedUser[],
		) => {
			if (fg('ptc-enable-teams-common-analytics-refactor')) {
				triggerAnalyticsForCreateTeamSuccessNext({
					numberOfMembers: invitedMembers.length,
					teamId: teamData.id,
					defaultTeamType: initialMembershipSettings || null,
					chosenTeamType: teamData.membershipSettings,
					teamTypeChoiceEnabled: enableMembershipSettingsChoice || null,
					...extraAnalyticsAttrs,
				});
			} else {
				triggerAnalyticsForCreateTeamSuccess(createAnalyticsEvent, invitedMembers.length, {
					teamId: teamData.id,
					defaultTeamType: initialMembershipSettings,
					chosenTeamType: teamData.membershipSettings,
					teamTypeChoiceEnabled: enableMembershipSettingsChoice,
					...extraAnalyticsAttrs,
				});
			}

			const result = onCreateTeamSuccess(teamData, invitedMembers, failedInvitedMembers);
			const requestedContainers = result !== undefined ? result?.requestedContainers : undefined;

			if (failedInvitedMembers && failedInvitedMembers.length > 0) {
				addFlag({
					title: formatMessage(messages.flagErrorCreateTeamPartialFailTitle),
					description: formatMessage(messages.flagErrorCreateTeamPartialFailDesc),
					appearance: 'error',
					id: 'create.team.failed',
				});
			}

			if (!disableSuccessFlag) {
				addFlag({
					title: formatMessage(messages.flagSuccessCreateTeamTitle),
					description: formatMessage(messages.flagSuccessCreateTeamDesc),
					appearance: 'success',
					id: 'create.team.success',
					actions: [
						{
							content: formatMessage(messages.flagSuccessCreateTeamAction),
							onClick: (event) => handleOnClickTeamLinkInFlag(event, teamData, invitedMembers),
							href: getTeamProfilePageLinkWithOrgId({
								remoteConfigResources,
								teamId: teamData.id,
								ref: product,
								src: AnalyticsSource.PEOPLE_TEAMS,
								orgId: props.orgId,
								containers: requestedContainers,
							}),
						},
					],
				});
			}
		},
		[
			createAnalyticsEvent,
			initialMembershipSettings,
			enableMembershipSettingsChoice,
			extraAnalyticsAttrs,
			onCreateTeamSuccess,
			disableSuccessFlag,
			addFlag,
			formatMessage,
			remoteConfigResources,
			props.orgId,
			product,
			handleOnClickTeamLinkInFlag,
			triggerAnalyticsForCreateTeamSuccessNext,
		],
	);

	const handleCreateTeamFailed = useCallback(
		(error: ErrorWithStatus, invitedMembers: InvitedUser[]) => {
			if (isSeriousError(error)) {
				if (fg('ptc-enable-teams-common-analytics-refactor')) {
					triggerAnalyticsForCreateTeamFailedNext({
						numberOfMembers: invitedMembers.length,
						errorMessage: error.message,
						errorStack: error.stack || null,
						errorStatus: error.status,
						traceId: error.traceId || null,
						...extraAnalyticsAttrs,
					});
				} else {
					triggerAnalyticsForCreateTeamFailed(createAnalyticsEvent, invitedMembers.length, {
						...extraAnalyticsAttrs,
						errorMessage: error.message,
						errorStack: error.stack,
						errorStatus: error.status,
						traceId: error.traceId,
					});
				}
			}

			onCreateTeamFailed(error, invitedMembers);

			if (addFlag) {
				addFlag({
					title: formatMessage(messages.flagErrorCreateTeamTitle),
					description: formatMessage(messages.flagErrorCreateTeamDesc),
					appearance: 'error',
					id: 'create.team.failed',
				});
			}
		},
		[
			onCreateTeamFailed,
			addFlag,
			createAnalyticsEvent,
			extraAnalyticsAttrs,
			formatMessage,
			triggerAnalyticsForCreateTeamFailedNext,
		],
	);

	return {
		onCreateTeamSuccess: handleCreateTeamSuccess,
		onCreateTeamFailed: handleCreateTeamFailed,
	};
}

export default useTeamCreateConfiguration;
